import React, { useEffect, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Download, Search, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, formatDate } from '../utils/dateFormat';

const AllJobCards = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    strictMode: false,
    dateType: 'appointment',
    from: '',
    to: '',
    search: '',
    status: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filteredJobCards, setFilteredJobCards] = useState([]);
  const [selectedJobCards, setSelectedJobCards] = useState([]);
  const [showBatchUpdate, setShowBatchUpdate] = useState(false);
  const [batchUpdateData, setBatchUpdateData] = useState({ status: '', technicianId: '' });
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    fetchJobCards();
    fetchTechnicians();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [jobCards, filters, sortConfig]);

  const fetchJobCards = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/job-cards?limit=1000');
      const jobCardsData = response.data.jobCards || response.data.data || response.data || [];
      setJobCards(Array.isArray(jobCardsData) ? jobCardsData : []);
    } catch (error) {
      toast.error('Failed to fetch job cards');
    } finally {
      setLoading(false);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get('/api/members?role=TECHNICIAN');
      setTechnicians(response.data.members || response.data || []);
    } catch (error) {
    }
  };

  const applyFiltersAndSort = () => {
    if (!Array.isArray(jobCards)) return;
    let filtered = [...jobCards];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(job => 
        job.orderNo?.toLowerCase().includes(searchLower) ||
        job.customer?.name?.toLowerCase().includes(searchLower) ||
        job.services?.some(s => s.service?.name?.toLowerCase().includes(searchLower)) ||
        job.parts?.some(p => (p.name || p.partName)?.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(job => job.status === filters.status);
    }

    // Apply date filters
    if (filters.from || filters.to) {
      filtered = filtered.filter(job => {
        let dateToCheck;
        switch (filters.dateType) {
          case 'appointment':
            dateToCheck = job.appointmentDate;
            break;
          case 'create':
            dateToCheck = job.createdAt;
            break;
          case 'complete':
            dateToCheck = job.completedAt;
            break;
          default:
            dateToCheck = job.appointmentDate;
        }
        
        if (!dateToCheck) return false;
        
        const jobDate = new Date(dateToCheck);
        const fromDate = filters.from ? new Date(filters.from) : null;
        const toDate = filters.to ? new Date(filters.to) : null;
        
        if (filters.strictMode) {
          const jobDateStr = jobDate.toDateString();
          if (fromDate && toDate) {
            return jobDateStr >= fromDate.toDateString() && jobDateStr <= toDate.toDateString();
          } else if (fromDate) {
            return jobDateStr >= fromDate.toDateString();
          } else if (toDate) {
            return jobDateStr <= toDate.toDateString();
          }
        } else {
          if (fromDate && toDate) {
            return jobDate >= fromDate && jobDate <= toDate;
          } else if (fromDate) {
            return jobDate >= fromDate;
          } else if (toDate) {
            return jobDate <= toDate;
          }
        }
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'orderNo':
            aValue = a.orderNo || '';
            bValue = b.orderNo || '';
            break;
          case 'appointmentDate':
            aValue = new Date(a.appointmentDate || 0);
            bValue = new Date(b.appointmentDate || 0);
            break;
          case 'customerName':
            aValue = a.customer?.name || '';
            bValue = b.customer?.name || '';
            break;
          case 'totalAmount':
            aValue = a.totalAmount || 0;
            bValue = b.totalAmount || 0;
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredJobCards(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-4 h-4" />;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };



  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedJobCards(filteredJobCards.map(job => job.id));
    } else {
      setSelectedJobCards([]);
    }
  };

  const handleSelectJob = (jobId, checked) => {
    if (checked) {
      setSelectedJobCards(prev => [...prev, jobId]);
    } else {
      setSelectedJobCards(prev => prev.filter(id => id !== jobId));
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedJobCards.length === 0) {
      toast.error('Please select job cards to update');
      return;
    }

    try {
      const updatePromises = selectedJobCards.map(jobId => {
        const updateData = {};
        if (batchUpdateData.status) updateData.status = batchUpdateData.status;
        if (batchUpdateData.technicianId) updateData.technicianId = parseInt(batchUpdateData.technicianId);
        
        return axios.put(`/api/job-cards/${jobId}`, updateData);
      });

      await Promise.all(updatePromises);
      toast.success(`${selectedJobCards.length} job cards updated successfully`);
      setSelectedJobCards([]);
      setShowBatchUpdate(false);
      setBatchUpdateData({ status: '', technicianId: '' });
      fetchJobCards();
    } catch (error) {
      toast.error('Failed to update job cards');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedJobCards.length === 0) {
      toast.error('Please select job cards to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedJobCards.length} job cards? This action cannot be undone.`)) {
      return;
    }

    try {
      const deletePromises = selectedJobCards.map(jobId => 
        axios.delete(`/api/job-cards/${jobId}`)
      );

      await Promise.all(deletePromises);
      toast.success(`${selectedJobCards.length} job cards deleted successfully`);
      setSelectedJobCards([]);
      fetchJobCards();
    } catch (error) {
      toast.error('Failed to delete job cards');
    }
  };

  const handleExport = () => {
    const csvContent = convertToCSV(filteredJobCards);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-cards-${formatDate(new Date())}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Job cards exported successfully');
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = 'Order No,App Date/Time,Name,Variant,Technician,Parts,Status';
    const rows = data.map(job => 
      `${job.orderNo},"${formatDateTime(job.appointmentDate)}","${job.customer?.name || ''}","${job.vehicle?.make || ''} ${job.vehicle?.model || ''}","${job.technician?.name || 'Not assigned'}","${job.parts?.map(p => p.name || p.partName).join(', ') || ''}","${job.status}"`
    );
    return [headers, ...rows].join('\n');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Job Cards</h1>
          <p className="text-sm text-gray-600 mt-1">
            Showing {filteredJobCards.length} of {jobCards.length} job cards
          </p>
        </div>
        <div className="flex space-x-2">
          {selectedJobCards.length > 0 && (
            <>
              <button
                onClick={() => setShowBatchUpdate(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Selected ({selectedJobCards.length})
              </button>
              {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                <button
                  onClick={handleBatchDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedJobCards.length})
                </button>
              )}
            </>
          )}
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-6 rounded-lg shadow border">
        {/* Strict Mode */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.strictMode}
              onChange={(e) => handleFilterChange('strictMode', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Strict Mode</span>
          </label>
        </div>

        {/* Date Filter Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Date filter by:</label>
          <div className="flex space-x-4">
            {[
              { value: 'appointment', label: 'Appointment Date' },
              { value: 'create', label: 'Create Date' },
              { value: 'complete', label: 'Complete Date' }
            ].map(({ value, label }) => (
              <label key={value} className="flex items-center">
                <input
                  type="radio"
                  name="dateType"
                  value={value}
                  checked={filters.dateType === value}
                  onChange={(e) => handleFilterChange('dateType', e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Date Range and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Order No, Customer, Service, Parts"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                strictMode: false,
                dateType: 'appointment',
                from: '',
                to: '',
                search: '',
                status: ''
              })}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Job Cards Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedJobCards.length === filteredJobCards.length && filteredJobCards.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('orderNo')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Order No.</span>
                      {getSortIcon('orderNo')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('appointmentDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>App. Date/Time</span>
                      {getSortIcon('appointmentDate')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customerName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer Name</span>
                      {getSortIcon('customerName')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Vehicle Variant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Technician
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Parts
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {getSortIcon('status')}
                    </div>
                  </th>
                  {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredJobCards.length === 0 ? (
                  <tr>
                    <td colSpan={(user?.role === 'ADMIN' || user?.role === 'MANAGER') ? "9" : "8"} className="px-6 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No job cards found'}
                    </td>
                  </tr>
                ) : (
                  filteredJobCards.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedJobCards.includes(job.id)}
                          onChange={(e) => handleSelectJob(job.id, e.target.checked)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/job-cards/${job.id}`)}>
                        {job.orderNo}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDateTime(job.appointmentDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {job.customer?.name || 'Default Customer'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {job.vehicle ? `${job.vehicle.make || ''} ${job.vehicle.model || ''}`.trim() : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {job.technician?.name || 'Not assigned'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {job.parts?.length > 0 ? (
                          <div className="space-y-1">
                            {job.parts.slice(0, 2).map((part, idx) => (
                              <div key={idx} className="text-xs">
                                {part.name || part.partName}
                              </div>
                            ))}
                            {job.parts.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{job.parts.length - 2} more
                              </div>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(job.status)}`}>
                          {job.status?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </td>
                      {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete job card ${job.orderNo}? This action cannot be undone.`)) {
                                axios.delete(`/api/job-cards/${job.id}`)
                                  .then(() => {
                                    toast.success('Job card deleted successfully');
                                    fetchJobCards();
                                  })
                                  .catch(error => {
                                    toast.error('Failed to delete job card');
                                  });
                              }
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Delete Job Card"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Batch Update Modal */}
      {showBatchUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Update Selected Job Cards ({selectedJobCards.length})</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={batchUpdateData.status}
                  onChange={(e) => setBatchUpdateData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Keep current status</option>
                  <option value="PENDING">Pending</option>
                  <option value="WAITING_FOR_PARTS">Waiting for Parts</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technician</label>
                <select
                  value={batchUpdateData.technicianId}
                  onChange={(e) => setBatchUpdateData(prev => ({ ...prev, technicianId: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Keep current technician</option>
                  {technicians.map(technician => (
                    <option key={technician.id} value={technician.id}>
                      {technician.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBatchUpdate(false);
                  setBatchUpdateData({ status: '', technicianId: '' });
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBatchUpdate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Update Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllJobCards;
